/**
 * Zero onboarding persistence (localStorage).
 *
 * Keys are platform-prefixed to avoid collisions with Ops / ESG / Admin:
 * - zero.onboarding.isNew.{userId}.{orgId}            → "true" | "false"
 * - zero.onboarding_state.{userId}.{orgId}            → JSON OnboardingState
 * - zero.onboarding.laterDismissed.{userId}.{orgId} → sessionStorage only ("1")
 *   Survives Refresh; cleared on logout so welcome returns after re-login.
 *
 * Legacy keys (migrated once / purged when modern keys exist):
 * - zero.onboarding.state.* (renamed → onboarding_state)
 * - zero.selfSetup.seen.* / done.* / active.* / phase.* / hubDismissed.*
 */

const IS_NEW_PREFIX = "zero.onboarding.isNew.";
const STATE_PREFIX = "zero.onboarding_state.";
/** Previous state key — migrated into STATE_PREFIX. */
const LEGACY_STATE_PREFIX = "zero.onboarding.state.";
const LATER_DISMISSED_PREFIX = "zero.onboarding.laterDismissed.";
/** Marks a tip as actively running in this browser tab session. */
const LIVE_SESSION_KEY = "zero.onboarding.liveSession";

/** Legacy prefixes — read once for migration, then purged. */
const LEGACY_SEEN_PREFIX = "zero.selfSetup.seen.";
const LEGACY_DONE_PREFIX = "zero.selfSetup.done.";
const LEGACY_ACTIVE_PREFIX = "zero.selfSetup.active.";
const LEGACY_PHASE_PREFIX = "zero.selfSetup.phase.";
const LEGACY_HUB_PREFIX = "zero.selfSetup.hubDismissed.";

const STEP_VALUES = ["dashboard", "report", "branchAnalysis"] as const;
export type OnboardingPage = (typeof STEP_VALUES)[number];

export type OnboardingSpot = string;

export type OnboardingState = {
  /**
   * Page of the current / last tip (`dashboard` | `report` | `branchAnalysis`),
   * or null on welcome hub / congrats.
   */
  page: OnboardingPage | null;
  /**
   * Exact tip/coachmark within that page (e.g. `monthSelect`, `download`),
   * or `hub` / `congrats`.
   */
  spot: OnboardingSpot;
  /** Completed tour step keys (for the stepper). */
  completed: OnboardingPage[];
  /** Mirror of last tip — used to resume after Later / logout / return. */
  resumePage?: OnboardingPage | null;
  resumeSpot?: OnboardingSpot;
};

const EMPTY_STATE: OnboardingState = {
  page: null,
  spot: "hub",
  completed: [],
  resumePage: null,
  resumeSpot: undefined,
};

function isNewKey(userId: string, organizationId: string): string {
  return `${IS_NEW_PREFIX}${userId}.${organizationId}`;
}

function stateKey(userId: string, organizationId: string): string {
  return `${STATE_PREFIX}${userId}.${organizationId}`;
}

function legacyStateKey(userId: string, organizationId: string): string {
  return `${LEGACY_STATE_PREFIX}${userId}.${organizationId}`;
}

function laterDismissedKey(userId: string, organizationId: string): string {
  return `${LATER_DISMISSED_PREFIX}${userId}.${organizationId}`;
}

function purgeLegacyKeys(userId: string, organizationId: string): void {
  localStorage.removeItem(`${LEGACY_SEEN_PREFIX}${userId}.${organizationId}`);
  localStorage.removeItem(`${LEGACY_DONE_PREFIX}${userId}.${organizationId}`);
  localStorage.removeItem(legacyStateKey(userId, organizationId));
  sessionStorage.removeItem(
    `${LEGACY_ACTIVE_PREFIX}${userId}.${organizationId}`,
  );
  sessionStorage.removeItem(
    `${LEGACY_PHASE_PREFIX}${userId}.${organizationId}`,
  );
  sessionStorage.removeItem(`${LEGACY_HUB_PREFIX}${userId}.${organizationId}`);
}

/** Remove every leftover legacy self-setup / renamed-state key in this browser. */
export function clearAllLegacySelfSetupKeys(): void {
  const localPrefixes = [
    LEGACY_SEEN_PREFIX,
    LEGACY_DONE_PREFIX,
    LEGACY_STATE_PREFIX,
  ];
  const sessionPrefixes = [
    LEGACY_ACTIVE_PREFIX,
    LEGACY_PHASE_PREFIX,
    LEGACY_HUB_PREFIX,
  ];

  const removeMatching = (
    storage: Storage,
    prefixes: string[],
  ): void => {
    const toRemove: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && prefixes.some((p) => key.startsWith(p))) {
        toRemove.push(key);
      }
    }
    for (const key of toRemove) {
      storage.removeItem(key);
    }
  };

  removeMatching(localStorage, localPrefixes);
  removeMatching(sessionStorage, sessionPrefixes);
}

function parsePage(raw: unknown): OnboardingPage | null {
  return typeof raw === "string" &&
    (STEP_VALUES as readonly string[]).includes(raw)
    ? (raw as OnboardingPage)
    : null;
}

function parseCompleted(raw: unknown): OnboardingPage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s) => parsePage(s))
    .filter((s): s is OnboardingPage => s != null);
}

function parseState(raw: string | null): OnboardingState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return {
      page: parsePage(parsed.page),
      spot:
        typeof parsed.spot === "string" && parsed.spot.length > 0
          ? parsed.spot
          : "hub",
      completed: parseCompleted(parsed.completed),
      resumePage:
        parsed.resumePage === undefined
          ? null
          : parsePage(parsed.resumePage),
      resumeSpot:
        typeof parsed.resumeSpot === "string"
          ? parsed.resumeSpot
          : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * One-time bridge from the old multi-key model into isNew + onboarding_state.
 */
function migrateFromLegacy(
  userId: string,
  organizationId: string,
): { isNew: boolean; state: OnboardingState } | null {
  const seen =
    localStorage.getItem(`${LEGACY_SEEN_PREFIX}${userId}.${organizationId}`) ===
    "1";
  const doneRaw = localStorage.getItem(
    `${LEGACY_DONE_PREFIX}${userId}.${organizationId}`,
  );
  const activeRaw = sessionStorage.getItem(
    `${LEGACY_ACTIVE_PREFIX}${userId}.${organizationId}`,
  );
  const phaseRaw = sessionStorage.getItem(
    `${LEGACY_PHASE_PREFIX}${userId}.${organizationId}`,
  );

  const hasLegacy = seen || doneRaw != null || activeRaw != null;
  if (!hasLegacy) return null;

  if (seen) {
    purgeLegacyKeys(userId, organizationId);
    return { isNew: false, state: EMPTY_STATE };
  }

  let completed: OnboardingPage[] = [];
  if (doneRaw) {
    try {
      completed = parseCompleted(JSON.parse(doneRaw) as unknown);
    } catch {
      completed = [];
    }
  }

  const page = parsePage(activeRaw);
  const spot = page && phaseRaw ? phaseRaw : "hub";

  purgeLegacyKeys(userId, organizationId);
  return {
    isNew: true,
    state: { page, spot: page ? spot : "hub", completed },
  };
}

/** `true` while onboarding is unfinished; missing key ⇒ treat as new. */
export function getOnboardingIsNew(
  userId: string,
  organizationId: string,
): boolean {
  const key = isNewKey(userId, organizationId);
  const raw = localStorage.getItem(key);
  if (raw === "true" || raw === "false") {
    // Modern keys present — drop any leftover old self-setup keys.
    purgeLegacyKeys(userId, organizationId);
    return raw === "true";
  }

  const migrated = migrateFromLegacy(userId, organizationId);
  if (migrated) {
    setOnboardingIsNew(userId, organizationId, migrated.isNew);
    if (migrated.isNew) {
      setOnboardingState(userId, organizationId, migrated.state);
    } else {
      clearOnboardingState(userId, organizationId);
    }
    return migrated.isNew;
  }

  setOnboardingIsNew(userId, organizationId, true);
  // Ensure onboarding_state always exists in localStorage for new users.
  if (!localStorage.getItem(stateKey(userId, organizationId))) {
    setOnboardingState(userId, organizationId, EMPTY_STATE);
  }
  return true;
}

export function setOnboardingIsNew(
  userId: string,
  organizationId: string,
  isNew: boolean,
): void {
  localStorage.setItem(
    isNewKey(userId, organizationId),
    isNew ? "true" : "false",
  );
}

export function getOnboardingState(
  userId: string,
  organizationId: string,
): OnboardingState {
  const existing = parseState(
    localStorage.getItem(stateKey(userId, organizationId)),
  );
  if (existing) {
    purgeLegacyKeys(userId, organizationId);
    return existing;
  }

  // Migrate renamed key zero.onboarding.state.* → zero.onboarding_state.*
  const legacyRaw = localStorage.getItem(legacyStateKey(userId, organizationId));
  const fromLegacyName = parseState(legacyRaw);
  if (fromLegacyName) {
    localStorage.removeItem(legacyStateKey(userId, organizationId));
    setOnboardingState(userId, organizationId, fromLegacyName);
    return fromLegacyName;
  }

  const migrated = migrateFromLegacy(userId, organizationId);
  if (migrated) {
    setOnboardingIsNew(userId, organizationId, migrated.isNew);
    if (migrated.isNew) {
      setOnboardingState(userId, organizationId, migrated.state);
      return migrated.state;
    }
    clearOnboardingState(userId, organizationId);
    return EMPTY_STATE;
  }

  // Persist empty hub state so the key is visible in localStorage.
  setOnboardingState(userId, organizationId, EMPTY_STATE);
  return EMPTY_STATE;
}

export function setOnboardingState(
  userId: string,
  organizationId: string,
  state: OnboardingState,
): void {
  localStorage.setItem(
    stateKey(userId, organizationId),
    JSON.stringify(state),
  );
  // Drop old key name if present.
  localStorage.removeItem(legacyStateKey(userId, organizationId));
}

export function clearOnboardingState(
  userId: string,
  organizationId: string,
): void {
  localStorage.removeItem(stateKey(userId, organizationId));
  localStorage.removeItem(legacyStateKey(userId, organizationId));
}

/** Persist progress while keeping isNew unchanged. */
export function patchOnboardingState(
  userId: string,
  organizationId: string,
  patch: Partial<OnboardingState>,
): OnboardingState {
  const current = getOnboardingState(userId, organizationId);
  const next: OnboardingState = {
    page: patch.page !== undefined ? patch.page : current.page,
    spot: patch.spot ?? current.spot,
    completed: patch.completed ?? current.completed,
    resumePage:
      patch.resumePage !== undefined ? patch.resumePage : current.resumePage,
    resumeSpot:
      patch.resumeSpot !== undefined ? patch.resumeSpot : current.resumeSpot,
  };
  setOnboardingState(userId, organizationId, next);
  return next;
}

/** Finish onboarding permanently (congrats dismissed). */
export function finishOnboarding(
  userId: string,
  organizationId: string,
): void {
  setOnboardingIsNew(userId, organizationId, false);
  clearOnboardingState(userId, organizationId);
  clearOnboardingLaterDismissed(userId, organizationId);
  clearOnboardingLiveSession();
  purgeLegacyKeys(userId, organizationId);
}

/**
 * Later: hide onboarding UI for this browser tab session (survives Refresh).
 * Cleared on logout so welcome can return after re-login.
 */
export function markOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): void {
  sessionStorage.setItem(laterDismissedKey(userId, organizationId), "1");
}

export function isOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): boolean {
  return (
    sessionStorage.getItem(laterDismissedKey(userId, organizationId)) === "1"
  );
}

export function clearOnboardingLaterDismissed(
  userId: string,
  organizationId: string,
): void {
  sessionStorage.removeItem(laterDismissedKey(userId, organizationId));
}

/** Clear every Later soft-hide flag (call on logout). */
export function clearAllOnboardingLaterDismissed(): void {
  const toRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (key?.startsWith(LATER_DISMISSED_PREFIX)) {
      toRemove.push(key);
    }
  }
  for (const key of toRemove) {
    sessionStorage.removeItem(key);
  }
}

/** Tip is running in this tab (survives Refresh; cleared on logout). */
export function markOnboardingLiveSession(): void {
  sessionStorage.setItem(LIVE_SESSION_KEY, "1");
}

export function isOnboardingLiveSession(): boolean {
  return sessionStorage.getItem(LIVE_SESSION_KEY) === "1";
}

export function clearOnboardingLiveSession(): void {
  sessionStorage.removeItem(LIVE_SESSION_KEY);
}
